import * as React from 'react';

import { enumUnit } from '@rematter/frontend-lib-api-client';
import { Content, DataGrid } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { $remoteFacilities } from '../../_state/facilities.state';
import { type QuantityFilter } from '../../_state/inventoryLevelsList.state';
import * as state from '../../_state/inventoryMovementHistory.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InventoryLevelRecord } from '../../types/inventoryLevel';

import { useFacilityBreakdownColumns } from './hooks/useFacilityBreakdownColumns';

//
// ⚛️ ⚛️ ⚛️ FACILITY BREAKDOWN TAB ⚛️ ⚛️ ⚛️
//
export const FacilityBreakdownTab = () => {
  // Translations
  const t = useTranslations();

  // States
  const [quantityFilter, setQuantityFilter] =
    React.useState<QuantityFilter>('all');

  // Atoms
  const facilityId = useAtomValue(state.$facilityId);
  const { data, isFetching, loadingState } = useAtomValue(
    state.$remoteFacilityBreakdown,
  );
  const { data: facilitiesData } = useAtomValue($remoteFacilities);

  // Data hooks
  const columns = useFacilityBreakdownColumns({
    quantityFilter,
    onQuantityFilterChange: setQuantityFilter,
  });

  // Memoized values
  const allLevels = React.useMemo(() => {
    const facilities = facilitiesData?.facilities ?? [];
    const inventoryLevels = data?.levels ?? [];
    const levelByFacility = new Map(
      inventoryLevels.filter((l) => l.facilityId).map((l) => [l.facilityId, l]),
    );

    const firstLevel = inventoryLevels[0];
    const unit = firstLevel?.quantityUnit ?? enumUnit.Pounds;

    return facilities.map(
      (fac): InventoryLevelRecord =>
        levelByFacility.get(fac.id) ?? {
          id: `${firstLevel?.itemId ?? 'unknown'}-${fac.id}`,
          itemId: firstLevel?.itemId ?? '',
          itemName: firstLevel?.itemName ?? '',
          itemSku: firstLevel?.itemSku ?? '',
          itemMeasurementUnit:
            firstLevel?.itemMeasurementUnit ?? enumUnit.Pounds,
          facilityId: fac.id,
          facilityName: fac.name,
          quantity: 0,
          quantityUnit: unit,
          currencyValues: [],
        },
    );
  }, [data?.levels, facilitiesData?.facilities]);

  const levels = React.useMemo(() => {
    let filtered = allLevels;
    if (facilityId && facilityId !== 'all')
      filtered = filtered.filter((l) => l.facilityId === facilityId);
    if (quantityFilter === 'hideZero')
      filtered = filtered.filter((l) => l.quantity !== 0);
    if (quantityFilter === 'negativeOnly')
      filtered = filtered.filter((l) => l.quantity < 0);
    return filtered;
  }, [allLevels, facilityId, quantityFilter]);

  return (
    <div className="@FacilityBreakdownTab flex h-full flex-col">
      <Content className="h-full p-4.5">
        <DataGrid<InventoryLevelRecord>
          isContained
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={levels}
          rowKey="id"
          aria-label={t.movementHistoryView.facilityBreakdown.facility}
          selectionMode="none"
          className="w-full text-sm"
          fullWidth
        />
      </Content>
    </div>
  );
};
